import axios from "axios";
import type {
  Company,
  UserData,
  RegisterResponse,
  LoginResponse,
  IndustriesResponse,
  CompanyResponse,
} from "@/types/auth";
import { store } from "@/store";
import {
  updateSubscriptionInfo,
} from "@/store/slices/authSlice";
import { setBlockedFeaturesMessage } from "@/store/slices/productSlice";

const API_BASE_URL = "https://api.thmn.ai/api";
const API_LOCAL_URL = "http://192.168.1.198:8000/api";

export interface ApiError {
  error: {
    detail: {
      [key: string]: string[];
    };
    status_code: number;
  };
}

const authApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add interceptor to include token in requests
authApi.interceptors.request.use((config) => {
  const state = store.getState();
  const token = state.auth.tokens.access;

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

// Added interceptor to handle subscription and usage data
authApi.interceptors.response.use(
  (response) => {
    if (response.data?.subscription || response.data?.subscription_usage) {
      store.dispatch(
        updateSubscriptionInfo({
          subscription: response.data.subscription || null,
          subscription_usage: response.data.subscription_usage || null,
        })
      );
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 403) {
      const blockedFeatures = error.response.data?.data?.blocked_features || 
      error.response.data?.data?.error?.detail?.blocked_features;

      if (blockedFeatures) {
        const message = Object.values(blockedFeatures)[0] as string;
        store.dispatch(setBlockedFeaturesMessage(message));
        window.location.href = "/upgrade";
      }
    }
    if (
      error.response?.data?.subscription ||
      error.response?.data?.subscription_usage
    ) {
      store.dispatch(
        updateSubscriptionInfo({
          subscription: error.response.data.subscription || null,
          subscription_usage: error.response.data.subscription_usage || null,
        })
      );
    }
    return Promise.reject(error);
  }
);

// Centralized API error handler
const handleApiError = (error: unknown): never => {
  console.log("ax", error);
  if (axios.isAxiosError(error) && error.response?.data) {
    throw error.response.data as ApiError;
  }
  throw {
    error: {
      detail: { general: ["Registration failed. Please try again."] },
      status_code: 500,
    },
  } as ApiError;
};

export const register = async (data: UserData): Promise<RegisterResponse> => {
  try {
    const response = await authApi.post("/accounts/users/register/", data);
    return response.data;
  } catch (error) {
    console.log("finam", error);
    handleApiError(error);
  }
};

export const login = async (data: {
  user_id: string;
  password: string;
}): Promise<LoginResponse> => {
  try {
    const response = await authApi.post("/accounts/users/login/", data);
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const verifyEmail = async (code: string) => {
  try {
    const response = await authApi.post("/accounts/users/verify/", {
      contact_mode: "EMAIL",
      code: code,
    });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const setupCompany = async (
  companyData: Company
): Promise<CompanyResponse> => {
  try {
    const response = await authApi.post("/companies/set-up/", companyData);
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};